import type { StateT as GeneratedStateT } from '../../../engine/bindings/typescript/tbs'
import type { State } from '../state/State.type'
import { StateMapDisk } from '../state/state_map_disk.enum'
import type { Engine } from './Engine.type'
import {
  resetEngineWasmSessionFromStateMessage,
  type EngineWasmSession,
} from './engineWasm'
import {
  decodeStateMessageBytesUnchecked,
  encodeGeneratedStateMessage,
  toGeneratedState,
} from './engineWasmCodec'

function replaceDerivedState(engine: Engine, nextState: State): void {
  engine.__setDerivedState(nextState)
}

function getPreferredLocalDisk(engine: Engine): StateMapDisk.IndexedDB | StateMapDisk.Memory | undefined {
  if (engine.state.map.disk === StateMapDisk.IndexedDB || engine.state.map.disk === StateMapDisk.Memory) {
    return engine.state.map.disk
  }
}

export function setStateFromBytes(engine: Engine, stateBytes: Uint8Array): Promise<EngineWasmSession> {
  replaceDerivedState(engine, decodeStateMessageBytesUnchecked(stateBytes, getPreferredLocalDisk(engine)))
  const sessionPromise = resetEngineWasmSessionFromStateMessage(engine, stateBytes)
  void sessionPromise.catch(() => undefined)
  return sessionPromise
}

/**
 * Bootstrap-only bytes path for engine-originated states that are intentionally
 * narrower than the app's full `StateSchema` requirements.
 */
export function setBootstrapStateFromBytes(engine: Engine, stateBytes: Uint8Array): Promise<EngineWasmSession> {
  replaceDerivedState(engine, decodeStateMessageBytesUnchecked(stateBytes, getPreferredLocalDisk(engine)))
  const sessionPromise = resetEngineWasmSessionFromStateMessage(engine, stateBytes)
  void sessionPromise.catch(() => undefined)
  return sessionPromise
}

export function setStateFromGenerated(engine: Engine, nextState: GeneratedStateT): Promise<EngineWasmSession> {
  return setStateFromBytes(engine, encodeGeneratedStateMessage(nextState))
}

export function setEngineStateFromExternal(engine: Engine, nextState: State): Promise<EngineWasmSession> {
  return setStateFromGenerated(engine, toGeneratedState(nextState))
}

export function setEngineStateFromWasm(engine: Engine, nextState: State): void {
  replaceDerivedState(engine, nextState)
}

export function setEngineStateForTeardown(engine: Engine, nextState: State): void {
  replaceDerivedState(engine, nextState)
}
